import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { useState } from "react";
import { ApiError } from "@/server/apiError";
import {
  changeEmail,
  confirmEmailChange,
  extractRetryAfter,
  RESEND_COOLDOWN_SECONDS,
} from "@/server/services/emailService";
import { VerificationCodeSchema, VerificationCodeType } from "@/schemas/auth";
import { useCountdown } from "@/hooks/useCountdown";

interface Props {
  newEmail: string;
  initialCooldown?: number;
  onSuccess: (maskedEmail: string) => void;
  onExpired: () => void;
  onEmailTaken: () => void;
  onUnavailable: () => void;
}

export default function EmailChangeCodeStep({
  newEmail,
  initialCooldown = RESEND_COOLDOWN_SECONDS,
  onSuccess,
  onExpired,
  onEmailTaken,
  onUnavailable,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { count: cooldown, start: startCooldown } =
    useCountdown(initialCooldown);
  const [isResending, setIsResending] = useState(false);

  const form = useForm<VerificationCodeType>({
    resolver: zodResolver(VerificationCodeSchema),
    defaultValues: { code: "" },
  });

  async function onSubmit({ code }: VerificationCodeType) {
    try {
      const result = await confirmEmailChange(code);
      onSuccess(result.email);
    } catch (error) {
      if (error instanceof ApiError) {
        const errorCode = error.data?.title as string | undefined;

        if (errorCode === "errors.EmailVerification.NotFound") {
          onExpired();
          return;
        }

        if (errorCode === "errors.EmailVerification.InvalidCode") {
          form.setError("code", {
            message: t("errors.EmailVerification.InvalidCode"),
          });
          return;
        }

        if (errorCode === "errors.EmailChange.EmailAlreadyTaken") {
          onEmailTaken();
          return;
        }

        // BE kill-switch (EMAIL_CHANGE_ENABLED=false) flipped mid-flow —
        // a 400 from the gate, not a bad code. Show the unavailable state.
        if (errorCode === "errors.EmailChange.NotEnabled") {
          onUnavailable();
          return;
        }

        toast({
          description: t([errorCode ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        toast({
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    }
  }

  async function handleResend() {
    setIsResending(true);
    try {
      await changeEmail({ newEmail });
      startCooldown(RESEND_COOLDOWN_SECONDS);
      form.reset();
    } catch (error) {
      if (error instanceof ApiError) {
        const errorCode = error.data?.title as string | undefined;

        if (errorCode === "errors.EmailVerification.CooldownActive") {
          startCooldown(extractRetryAfter(error.data));
          return;
        }

        if (errorCode === "errors.EmailChange.NotEnabled") {
          onUnavailable();
          return;
        }

        toast({
          description: t([errorCode ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        toast({ description: t("errorOccurred"), variant: "destructive" });
      }
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="my-7 space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">
          {t("register.codeStep.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("changeEmail.codeSentTo", { email: newEmail })}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="rounded-lg border-border bg-card py-6 text-center tracking-widest placeholder:opacity-70"
                    placeholder={t("register.codeStep.codePlaceholder")}
                    inputMode="numeric"
                    maxLength={6}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("register.codeStep.verifying")
              : t("register.codeStep.verifyButton")}
          </Button>
        </form>
      </Form>

      <div className="flex items-center justify-end text-sm">
        <button
          type="button"
          disabled={cooldown > 0 || isResending}
          className="text-secondary underline disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleResend}
        >
          {cooldown > 0
            ? t("register.codeStep.resendIn", { seconds: cooldown })
            : t("register.codeStep.resendCode")}
        </button>
      </div>
    </div>
  );
}
